import React, { useEffect, useMemo, useState } from "react";
import { buildVaultQuizViewModel } from "./qmsVaultWorkspaceUtils";
import QmsReviewDueDatePicker from "./QmsReviewDueDatePicker";
import {
  buildReviewNotificationRules,
  deriveReviewDurationFromDueDate,
  formatQuizDueDateForApi,
  normalizeReminderInterval,
  parseReviewDueDateInput,
} from "./qmsPublishReviewNotificationUtils";

const PUBLISH_FORM_ID = "qms-publish-confirm-form";

function QmsPublishConfirmForm({
  row,
  workspaceResponse,
  saving = false,
  myLearningCount = 0,
  libraryCount = 0,
  onSubmit,
}) {
  const [comments, setComments] = useState("");
  const [reviewDueDateValue, setReviewDueDateValue] = useState("");
  const [isMandatory, setIsMandatory] = useState(true);
  const [isQuizApplicable, setIsQuizApplicable] = useState(true);
  const [notificationRules, setNotificationRules] = useState(() =>
    buildReviewNotificationRules(null),
  );
  const [error, setError] = useState("");

  const quizModel = useMemo(
    () => buildVaultQuizViewModel(workspaceResponse || {}),
    [workspaceResponse],
  );
  const hasLinkedQuiz = Boolean(
    quizModel?.linkedQuiz?.quizId &&
      (quizModel?.settings?.hasQuestions || quizModel?.questions?.length),
  );
  const showQuizWarning = isQuizApplicable && !hasLinkedQuiz;

  const reviewDueDate = useMemo(
    () => parseReviewDueDateInput(reviewDueDateValue),
    [reviewDueDateValue],
  );

  useEffect(() => {
    if (!row?.DocumentId) return;
    setComments("");
    setReviewDueDateValue("");
    setIsMandatory(true);
    setIsQuizApplicable(true);
    setError("");
  }, [row?.DocumentId]);

  useEffect(() => {
    setNotificationRules((current) => {
      const rebuilt = buildReviewNotificationRules(reviewDueDate);
      if (!current.length) return rebuilt;
      return rebuilt.map((rule) => {
        const previous = current.find((item) => item.triggerCode === rule.triggerCode);
        return previous ? { ...rule, isEnabled: previous.isEnabled } : rule;
      });
    });
  }, [reviewDueDate]);

  const toggleNotificationRule = (index) => {
    setNotificationRules((current) =>
      current.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, isEnabled: !rule.isEnabled } : rule,
      ),
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!reviewDueDateValue) {
      setError("Select a review due date.");
      return;
    }

    if (!reviewDueDate) {
      setError("Could not determine a valid review due date.");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (reviewDueDate < today) {
      setError("Review due date cannot be in the past.");
      return;
    }

    const derived = deriveReviewDurationFromDueDate(new Date(), reviewDueDate);
    if (!derived) {
      setError("Could not derive review duration from the selected date.");
      return;
    }

    if (showQuizWarning) {
      setError("Link a quiz in the Quiz panel or turn off Quiz.");
      return;
    }

    setError("");
    onSubmit?.({
      comments: comments.trim(),
      reviewDateNumber: derived.reviewDateNumber,
      reviewDateSuffix: derived.reviewDateSuffix,
      reviewDueDate: formatQuizDueDateForApi(reviewDueDate),
      isMandatory,
      isQuizApplicable,
      quizDueDate: formatQuizDueDateForApi(reviewDueDate),
      reminderFrequency: "",
      reminderInterval: null,
      notificationRules: notificationRules
        .filter((rule) => rule.scheduledDate && rule.isEnabled !== false)
        .map((rule, index) => ({
          triggerCode: rule.triggerCode,
          triggerLabel: rule.triggerLabel,
          scheduledDate: formatQuizDueDateForApi(rule.scheduledDate),
          recipients: rule.recipients,
          channel: rule.channel,
          isEnabled: rule.isEnabled !== false,
          sortOrder: rule.sortOrder ?? index,
        })),
    });
  };

  return (
    <form
      id={PUBLISH_FORM_ID}
      className="qms-publish-confirm-form qms-publish-confirm-form--compact qms-publish-confirm-form--fit"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="qms-publish-confirm-form__toolbar">
        <div className="qms-publish-confirm-form__summary">
          <span>
            <strong>{myLearningCount}</strong> ML
          </span>
          <span>
            <strong>{libraryCount}</strong> Lib
          </span>
        </div>

        <QmsReviewDueDatePicker
          inline
          value={reviewDueDateValue}
          disabled={saving}
          onChange={setReviewDueDateValue}
        />

        <div className="qms-publish-confirm-form__checks">
          <label className="qms-publish-confirm-form__check">
            <input
              type="checkbox"
              checked={isMandatory}
              disabled={saving}
              onChange={(event) => setIsMandatory(event.target.checked)}
            />
            <span>Mandatory</span>
          </label>
          <label
            className={`qms-publish-confirm-form__check${
              showQuizWarning ? " has-warning" : ""
            }`}
          >
            <input
              type="checkbox"
              checked={isQuizApplicable}
              disabled={saving}
              onChange={(event) => setIsQuizApplicable(event.target.checked)}
            />
            <span>Quiz</span>
          </label>
        </div>
      </div>

      {showQuizWarning ? (
        <p className="qms-publish-confirm-form__quiz-warn" role="alert">
          <i className="fas fa-exclamation-triangle" aria-hidden="true" />
          No quiz linked — open the Quiz panel to link one, or turn off Quiz.
        </p>
      ) : null}

      <div className="qms-publish-notif-panel qms-publish-notif-panel--grid">
        <div className="qms-publish-notif-panel__head">
          <h6 className="qms-publish-notif-panel__title">Notification rules</h6>
          <span className="qms-publish-notif-panel__meta">My Learning · Email</span>
        </div>
        <div className="qms-publish-notif-panel__grid">
          {notificationRules.map((rule, index) => (
            <div key={rule.triggerCode} className="qms-publish-notif-panel__rule">
              <div className="qms-publish-notif-panel__rule-main">
                <strong>{rule.triggerCode}</strong>
                <span>{rule.scheduledDateLabel}</span>
              </div>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  disabled={saving}
                  checked={rule.isEnabled !== false}
                  aria-label={`Enable ${rule.triggerCode}`}
                  onChange={() => toggleNotificationRule(index)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <input
        id="qms-publish-comments"
        type="text"
        className="form-control form-control-sm qms-publish-confirm-form__comments-input"
        value={comments}
        disabled={saving}
        placeholder="Comments (optional)"
        onChange={(event) => setComments(event.target.value)}
      />

      {error ? (
        <p className="qms-publish-confirm-form__error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

export { PUBLISH_FORM_ID };
export default QmsPublishConfirmForm;
